import { useMutation, useSuspenseQuery } from "@tanstack/react-query"
import {
  createFileRoute,
  Link,
  redirect,
  useRouter,
} from "@tanstack/react-router"
import { GatedRegisterSessionPOSTSuccessResponse } from "ipctg-inflow-types/InFlowGatedAPI"
import { useEffect } from "react"
import { Controller, FormProvider, useForm, useWatch } from "react-hook-form"
import { Trans, useTranslation } from "react-i18next"

import api from "@/utilities/api"

import { Button } from "@/globalComponents/atoms/Button"
import Card from "@/globalComponents/atoms/Card"
import Checkbox from "@/globalComponents/atoms/Checkbox"
import FieldError from "@/globalComponents/atoms/FieldError"
import Heading from "@/globalComponents/atoms/Heading"
import Icon from "@/globalComponents/atoms/Icon"
import Label from "@/globalComponents/atoms/Label"
import Step from "@/globalComponents/atoms/Step"
import Text from "@/globalComponents/atoms/Text"
import TextField from "@/globalComponents/molecules/TextField"
import {
  GatedLookupSessionsError,
  GatedRegisterSessionError,
} from "@/utilities/expectedErrors"
import { requiredRule } from "@/utilities/form"
import { meQueryOptions } from "@/utilities/hooks/queries/user"
import useToggle from "@/utilities/hooks/useToggle"
import {
  normalizePhoneNumber,
  removeUSCountryCode,
  stripNonPhoneCharacters,
} from "@/utilities/normalizers"

import { NoPhoneBasedSessionModal } from "../-components/NoPhoneBasedSessionModal"

export const Route = createFileRoute(
  "/qr/$qrLookupID/gated/register/contact-info"
)({
  staticData: {
    pageName: "Contact Information",
  },
  validateSearch: (
    search: Record<string, unknown>
  ): { plate: string; sessionID?: string } => {
    return {
      plate: (search.plate as string) || "",
      sessionID: (search.sessionID as string) || "",
    }
  },
  beforeLoad: ({ search: { plate }, params: { qrLookupID } }) => {
    if (!plate) {
      throw redirect({
        to: "/qr/$qrLookupID/gated/register/look-up",
        params: { qrLookupID },
      })
    }
  },
  component: RouteComponent,
})

interface RegistrationFormSchema {
  plate: string
  phone: string
  sessionID: string
  createAccount: boolean
}

function RouteComponent() {
  const navigate = Route.useNavigate()
  const router = useRouter()
  const { qrLookupID } = Route.useParams()
  const { plate, sessionID } = Route.useSearch()
  const { t } = useTranslation(["gated", "common", "errors"])
  const [showModal, toggleModal] = useToggle(false)
  const { data: me } = useSuspenseQuery(meQueryOptions())

  const requiresPhoneChallenge = !sessionID

  const form = useForm<RegistrationFormSchema>({
    defaultValues: {
      plate,
      phone:
        !requiresPhoneChallenge && me?.cognitoUser.phone_number
          ? normalizePhoneNumber(
              removeUSCountryCode(me.cognitoUser.phone_number)
            )
          : "",
      sessionID,
      createAccount: false,
    },
    mode: "onBlur",
  })

  const phone = useWatch({
    control: form.control,
    name: "phone",
    defaultValue: "",
  })

  const { mutate, isPending, reset } = useMutation({
    mutationFn,
    onSuccess,
    throwOnError: true,
  })

  useEffect(() => {
    form.reset(undefined, { keepValues: true })
  }, [form.formState.isSubmitSuccessful])

  return (
    <>
      <Heading className="center" level={3} as={1}>
        {t("register.contactInfo.header", { ns: "gated" })}
      </Heading>
      <Step step={2} of={4} />
      <Card>
        <Heading level={1} as={2}>
          {requiresPhoneChallenge
            ? t("register.contactInfo.subhead.challenge", { ns: "gated" })
            : t("register.contactInfo.subhead.contact", { ns: "gated" })}
        </Heading>
        {requiresPhoneChallenge && (
          <Text>
            <Trans i18nKey="register.contactInfo.description" ns="gated">
              If you used a phone number to enter the lot, please use the same
              number here. <strong>We need it to verify your session.</strong>
            </Trans>
          </Text>
        )}
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <fieldset disabled={isPending}>
              <input type="hidden" {...form.register("plate")} />
              <input type="hidden" {...form.register("sessionID")} />
              <Controller<RegistrationFormSchema, "phone">
                name="phone"
                rules={{
                  required: requiredRule(
                    t("common.required", { ns: "errors" })
                  ),
                }}
                render={({
                  field: { ref, ...rest },
                  fieldState: { error, invalid },
                }) => (
                  <TextField {...rest} isInvalid={invalid}>
                    <Label>
                      {t("register.contactInfo.fieldLabel", { ns: "gated" })}
                    </Label>
                    {/* Thin space cause safari :/ */}
                    <TextField.Phone
                      innerRef={ref}
                      placeholder="​(123) 456-7890"
                    />
                    <FieldError>{error?.message}</FieldError>
                  </TextField>
                )}
              />
              {!me && (
                <Controller<RegistrationFormSchema, "createAccount">
                  name="createAccount"
                  render={({ field: { value, onChange, ref } }) => (
                    <Checkbox
                      id="createAccount"
                      innerRef={ref}
                      value={!!value}
                      onChange={onChange}
                      isDisabled={isPending}
                    >
                      {t("register.contactInfo.createAccount", { ns: "gated" })}
                    </Checkbox>
                  )}
                />
              )}
              <Button type="submit" buttonType="primary" isPending={isPending}>
                {t("common.continue", { ns: "common" })}
                <Icon name="arrowRight" slot="right" />
              </Button>
            </fieldset>
          </form>
        </FormProvider>
        {requiresPhoneChallenge && (
          <Text level={2}>
            {t("register.contactInfo.forgotChallenge", { ns: "gated" })}{" "}
            <b>
              <Link from={Route.fullPath} to="/qr/$qrLookupID/help">
                {t("common.clickHere", { ns: "common" })}.
              </Link>
            </b>
          </Text>
        )}
      </Card>
      <NoPhoneBasedSessionModal
        isOpen={showModal}
        onOpenChange={toggleModal}
        phone={phone}
        resetMutation={reset}
        onExit={() =>
          navigate({
            to: "/qr/$qrLookupID/welcome",
            replace: true,
          })
        }
      />
    </>
  )

  async function ensureSessionID(data: RegistrationFormSchema) {
    // Already have one? Great.
    if (data.sessionID) return data.sessionID

    const phone = stripNonPhoneCharacters(data.phone)
    const lookupRsp = await api.gated.lookupSessions.POST({
      ...data,
      phone,
      method: "phone",
    })
    if ("failReason" in lookupRsp) {
      throw new GatedLookupSessionsError(lookupRsp.failReason)
    }
    const session = lookupRsp.sessions.find((s) => !s.isRegistered)
    if (!session) {
      toggleModal()
      return null
    }
    return session.sessionID
  }

  async function onSubmit(data: RegistrationFormSchema) {
    mutate({ ...data })
  }

  async function mutationFn(data: RegistrationFormSchema) {
    const phone = stripNonPhoneCharacters(data.phone)
    const sessionID = await ensureSessionID(data)

    if (!sessionID) {
      return // Modal shown
    }

    const registerRsp = await api.gated.registerSession.POST({
      sessionID,
      phone,
      isInLane: false,
      isReregistering: false,
    })

    if ("failReason" in registerRsp) {
      throw new GatedRegisterSessionError(registerRsp.failReason)
    }

    return registerRsp
  }

  function onSuccess(
    rsp: GatedRegisterSessionPOSTSuccessResponse | undefined,
    variables: RegistrationFormSchema
  ) {
    const sessionID = rsp?.session.sessionID
    if (!sessionID) {
      return
    }
    if (variables.createAccount && !me) {
      const { href: cartHref } = router.buildLocation({
        to: "/qr/$qrLookupID/gated/register/$sessionID/cart",
        params: { qrLookupID, sessionID },
        search: { express: false },
      }) // build a typesafe URL for redirect

      navigate({
        to: "/auth",
        search: {
          qrLookupID,
          redirect: cartHref,
          phone: stripNonPhoneCharacters(variables.phone),
          email: undefined,
          plate: variables.plate,
          conversionFlow: "gatedRegister",
        },
      })
    } else {
      navigate({
        to: "/qr/$qrLookupID/gated/register/$sessionID/cart",
        params: { sessionID: rsp.session.sessionID },
        search: { express: false },
      })
    }
  }
}
